import React, { useEffect, useRef, useState } from "react";
import downArrowIcon from "../../../../assets/Filter/downArrow.svg";
import crossIcon from "../../../../assets/cross.svg";
import CloseIcon from "../../../../assets/CloseIcon.svg";
import Checkbox from "../../../../components/CheckBox/index2";
import { Spinner } from "../../../../components/Spinner";
import { GET } from "../../../../utils/webService";

const PAGE_LIMIT = 20;

function RemoteSearchFilterAccordian({
  data,
  currentStatus,
  filterModal,
  persistFilterData,
  setPersistFilterData,
  setFilterPayLoad,
}: any) {
  const [filterData, setFilterData] = useState(data || {});
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const debounceTimer = useRef<any>(null);
  const requestId = useRef(0);

  const selectedValues = persistFilterData?.[filterData?.label] || [];

  const addUniqueValue = (values: any[], value: any) => {
    return values.includes(value) ? values : [...values, value];
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const updateFilterPayload = (value: any, isAlreadySelected: boolean) => {
    setFilterPayLoad((prevPayload: any) => {
      const filterArrOne = [...(prevPayload?.filterArrOne || [])];
      const index = filterArrOne.findIndex(
        (filter: any) => Object.keys(filter)[0] === filterData?.filterKey
      );

      const existingValues =
        index > -1
          ? [...(filterArrOne[index]?.[filterData?.filterKey]?.$in || [])]
          : [];

      const updatedValues = isAlreadySelected
        ? existingValues.filter((item: any) => item !== value)
        : addUniqueValue(existingValues, value);

      if (index > -1) {
        if (updatedValues.length > 0) {
          filterArrOne[index] = {
            [filterData?.filterKey]: { $in: updatedValues },
          };
        } else {
          filterArrOne.splice(index, 1);
        }
      } else if (updatedValues.length > 0) {
        filterArrOne.push({
          [filterData?.filterKey]: { $in: updatedValues },
        });
      }

      return {
        ...prevPayload,
        filterArrOne,
        filterArrTwo: [...(prevPayload?.filterArrTwo || [])],
      };
    });
  };

  const updatePersistedSelection = (value: any, isAlreadySelected: boolean) => {
    setPersistFilterData((prevData: any) => {
      const existingValues = prevData?.[filterData?.label] || [];
      const updatedValues = isAlreadySelected
        ? existingValues.filter((item: any) => item !== value)
        : addUniqueValue(existingValues, value);

      return { ...prevData, [filterData?.label]: updatedValues };
    });
  };

  const toggleValue = (value: any, isAlreadySelected: boolean) => {
    updateFilterPayload(value, isAlreadySelected);
    updatePersistedSelection(value, isAlreadySelected);

    setFilterData((prevData: any) => ({
      ...prevData,
      menu: (prevData?.menu || []).map((item: any) =>
        item?.value === value
          ? { ...item, isActive: !isAlreadySelected }
          : item
      ),
    }));
  };

  const buildMenu = (responseData: any[]) => {
    const persistedValues = persistFilterData?.[filterData?.label] || [];

    return (
      responseData?.map((item: any) => ({
        name: item?.label,
        value: item?.value,
        isActive: persistedValues.includes(item?.value),
      })) || []
    );
  };

  const mergeMenu = (existingMenu: any[], nextMenu: any[]) => {
    const mergedMenu = [...existingMenu];

    nextMenu.forEach((nextItem: any) => {
      const index = mergedMenu.findIndex(
        (existingItem: any) => existingItem?.value === nextItem?.value
      );

      if (index > -1) {
        mergedMenu[index] = nextItem;
      } else {
        mergedMenu.push(nextItem);
      }
    });

    return mergedMenu;
  };

  const fetchListHandler = async (
    inputData: string,
    nextPageNo: number = 1,
    append: boolean = false
  ) => {
    const trimmedInput = inputData.trim();
    const currentRequestId = requestId.current + 1;
    requestId.current = currentRequestId;

    if (append) {
      setIsPaginationLoading(true);
    } else {
      setIsLoading(true);
      setIsPaginationLoading(false);
      setHasMore(true);
    }

    try {
      const skip = (nextPageNo - 1) * PAGE_LIMIT;
      const url = `${filterData?.apiUrl}?currentStatus=${encodeURIComponent(
        currentStatus || "DRAFT"
      )}&searchValue=${encodeURIComponent(
        trimmedInput
      )}&pageNo=${nextPageNo}&skip=${skip}&limit=${PAGE_LIMIT}`;

      const response = await GET(url, {
        headers: { "Content-Type": "application/json" },
      });

      if (currentRequestId !== requestId.current) {
        return;
      }

      if (response?.data?.success) {
        const nextMenu = buildMenu(response?.data?.data || []);

        setFilterData((prevData: any) => ({
          ...prevData,
          menu: append ? mergeMenu(prevData?.menu || [], nextMenu) : nextMenu,
        }));
        setPageNo(nextPageNo);
        setHasMore(nextMenu.length >= PAGE_LIMIT);
      } else {
        setHasMore(false);
        if (append) {
          return;
        }
        setFilterData((prevData: any) => ({ ...prevData, menu: [] }));
      }
    } catch (error) {
      setHasMore(false);
      if (append) {
        return;
      }
      setFilterData((prevData: any) => ({ ...prevData, menu: [] }));
    } finally {
      if (currentRequestId === requestId.current) {
        if (append) {
          setIsPaginationLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  };

  const searchListHandler = async (inputData: string) => {
    setSearchInput(inputData);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      fetchListHandler(inputData, 1, false);
    }, 500);
  };

  const onOptionsScroll = (e: any) => {
    const target = e.currentTarget;
    const isNearBottom =
      target.scrollHeight - target.scrollTop - target.clientHeight < 40;

    if (!isNearBottom || isLoading || isPaginationLoading || !hasMore) {
      return;
    }

    fetchListHandler(searchInput, pageNo + 1, true);
  };

  useEffect(() => {
    if (filterModal) {
      searchListHandler("");
    }
  }, [filterModal, currentStatus]);

  return (
    <div>
      <div className="flex flex-col shadow-sm rounded-xl cursor-pointer">
        <div
          className={`flex items-center justify-between gap-3 px-3 py-3 border border-gray-200 ${
            filterData?.isCollapse
              ? "bg-[#f6f6f6] rounded-t-xl rounded-b-none"
              : "bg-white rounded-xl"
          }`}
        >
          <p
            className="font-semibold flex-1 text-[14px] text-[#1C1C1C] leading-tight"
            onClick={() => {
              setFilterData((prevData: any) => ({
                ...prevData,
                isCollapse: !prevData?.isCollapse,
              }));
            }}
          >
            {filterData?.name}
          </p>

          <div
            className={`flex ${
              filterData?.isCollapse && "border shadow-md"
            } rounded-lg`}
          >
            <div
              className={`flex items-center rounded-l-lg ${
                filterData?.isCollapse ? "bg-[#F6F6F6]" : "bg-white"
              }`}
            >
              {filterData?.isCollapse && (
                <>
                  <div className="flex min-w-0 w-full border-r rounded-l-md bg-white h-full text-left">
                    <input
                      placeholder="Search..."
                      value={searchInput}
                      onChange={(e: any) => searchListHandler(e.target.value)}
                      className="w-full search-input-cl border-none rounded-md h-full text-[14px] py-2 px-3 bg-white"
                    />
                  </div>
                  <div className="flex items-center justify-center">
                    <img
                      src={crossIcon}
                      alt="clear-search"
                      onClick={() => searchListHandler("")}
                      className="w-[24px] mx-2"
                    />
                  </div>
                </>
              )}
            </div>
            <div
              className="flex py-2 px-3 border-l rounded-r hover:bg-[#F6F6F6] bg-white"
              onClick={() => {
                setFilterData((prevData: any) => ({
                  ...prevData,
                  isCollapse: !prevData?.isCollapse,
                }));
              }}
            >
              <img
                src={downArrowIcon}
                alt="downArrowIcon"
                className={`transform w-[18px] transition-transform duration-400 ${
                  filterData?.isCollapse ? "rotate-180" : ""
                }`}
              />
            </div>
          </div>
        </div>

        {filterData?.isCollapse && (
          <>
            {selectedValues.length > 0 && (
              <div className="border p-4 grid grid-cols-2 sm:grid-cols-3 gap-y-2 gap-x-2 bg-white">
                {selectedValues.map((item: any, i: number) => (
                  <div
                    className="border text-[14px] flex justify-between items-center px-2 py-1 rounded min-w-0"
                    key={`${item}_${i}`}
                  >
                    <div className="truncate">{item}</div>
                    <button
                      className="w-[15px] flex-shrink-0"
                      onClick={() => toggleValue(item, true)}
                    >
                      <img src={CloseIcon} alt="" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              onScroll={onOptionsScroll}
              className={`border-b py-0 border-r border-l grid ${
                filterData?.menu?.length > 0 ? "grid-cols-1 sm:grid-cols-2" : ""
              } max-h-[300px] overflow-y-auto overflow-x-hidden rounded-bl-md rounded-br-md bg-white`}
            >
              {isLoading ? (
                <div className="py-4 flex justify-center text-center font-Open w-[100%] font-semibold">
                  <Spinner />
                </div>
              ) : filterData?.menu?.length > 0 ? (
                filterData?.menu?.map((subMenu: any, index: number) => (
                  <div className="px-2" key={`${subMenu?.value}_${index}`}>
                    <button
                      className={`flex items-center cursor-pointer w-full border gap-3 min-h-[52px] py-2 ${
                        index !== filterData?.menu?.length - 1
                          ? "border-b-[#E8E8E8]"
                          : "border-b-0"
                      } border-t-0 border-r-0 border-l-0`}
                      onClick={() =>
                        toggleValue(subMenu?.value, subMenu?.isActive)
                      }
                    >
                      <Checkbox
                        className="px-4"
                        checkboxClassName="gap-1"
                        name={subMenu?.name}
                        checked={subMenu?.isActive}
                      />
                      <p
                        className="font-bold text-[14px] text-[#323232] text-left min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                        title={subMenu?.name}
                      >
                        {subMenu?.name}
                      </p>
                    </button>
                  </div>
                ))
              ) : (
                <div className="py-4 border-t-0 cursor-not-allowed flex justify-center text-center font-Open w-[100%] font-semibold">
                  No Data Found
                </div>
              )}
              {isPaginationLoading && (
                <div className="py-4 flex justify-center text-center font-Open w-[100%] font-semibold col-span-1 sm:col-span-2">
                  <Spinner />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default RemoteSearchFilterAccordian;
